import React, { useState, useEffect } from "react";
import Layout from "../../components/Layout/Layout";
import { db } from "../../firebase/firebase-config";
import { useAuthState } from 'react-firebase-hooks/auth';
import { collection, query, where, getDocs, getDoc, deleteDoc, doc } from "firebase/firestore";
import { Table, Input, Select, Card, Space, Button, Empty, Spin } from 'antd';
import { auth } from '../../firebase/firebase-config';
import BeatLoader from "react-spinners/BeatLoader";
import AddFacilityModal from "./AddFacilityModal/AddFacilityModal";
import "tippy.js/dist/tippy.css";
import { ConfirmModal } from "../../components";
import DataTable from "./table/DataTable";
import moment from 'moment';
import { toast } from "react-toastify";
import { getAllFacilities } from "../../services/facilities";
import { FaHospital, FaPlus } from 'react-icons/fa';
import './facilities.css'

const { Search } = Input;

interface Facility {
  id: string;
  name: string;
  address?: string;
  phone?: string;
  [key: string]: any;
}

function Index(): JSX.Element {
  // State management
  const [allFacilities, setAllFacilities] = useState<Facility[]>([]);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>('');
  const [filteredData, setFilteredData] = useState<Facility[]>([]);
  const [editData, setEditData] = useState<Facility | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [currentActiveId, setCurrentActiveId] = useState<string | undefined>();
  const [loading, setLoading] = useState<boolean>(false);
  
  // Firebase auth
  const [user] = useAuthState(auth);

  // Handle edit facility
  const handleEdit = (facilityData: Facility): void => {
    setEditData(facilityData);
    setShowModal(true);
  };

  // Toggle add/edit modal
  const toggleAddModal = (): void => {
    setEditData(null);
    setShowModal(!showModal);
  };

  // Fetch facilities data
  const fetchData = async (): Promise<void> => {
    setLoading(true);
    setFilteredData([]); // Clear the table while loading

    try {
      const response: any = await getAllFacilities();
      if (response?.data?.facilities) {
        console.log('ALL FACILITIES: ', response?.data?.facilities)
        const facilitiesWithIndex = response.data.facilities.map((facility: Facility, index: number) => ({
          ...facility,
          index: index + 1
        }));
        setAllFacilities(facilitiesWithIndex);
      } else {
        setAllFacilities([]);
        toast.error("Failed to fetch facilities data");
      }
    } catch (error: any) {
      console.error("Error fetching facilities:", error);
      toast.error(error.message || "An error occurred while fetching facilities");
      setAllFacilities([]);
    } finally {
      setLoading(false);
    }
  };

  // Delete facility function (currently a placeholder)
  const deleteFacility = async (): Promise<void> => {
    try {
      if (currentActiveId) {
        // Implement your delete logic here
        // const docRef = doc(collection(db, "Facilities"), currentActiveId);
        // await deleteDoc(docRef);
        
        toast.success('Facility deleted successfully');
        fetchData();
        setShowConfirmModal(false);
      }
    } catch (error: any) {
      console.error("Error deleting facility:", error);
      toast.error(error.message || "Failed to delete facility");
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchData();
  }, []);

  // Filter data when search text or facilities change
  useEffect(() => {
    const filtered = filterData(allFacilities, searchText);
    setFilteredData(filtered);
  }, [allFacilities, searchText]);

  // Filter function for search
  const filterData = (data: Facility[], query: string): Facility[] => {
    if (!query) return data;

    return data.filter((item) =>
      Object.values(item).some((value) =>
        value?.toString().toLowerCase().includes(query.toLowerCase())
      )
    );
  };

  // Handle search input
  const handleSearch = (value: string): void => {
    setSearchText(value);
  };

  // Get facility count
  const getFacilityCount = (): number => {
    return allFacilities.length;
  };

  return (
    <>
      <Layout title="Facilities" pageTitle="All Facilities">
        <div className="section">
          {/* Summary Card */}
          <div className="summary-cards" style={{ marginBottom: '24px' }}>
            <Card 
              style={{ 
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                borderRadius: '8px',
                display: 'flex'
              }}
            >
              <div style={{ 
                display: 'flex', 
                alignItems: 'center' 
              }}>
                <div style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '48px',
                  height: '48px',
                  borderRadius: '10px',
                  marginRight: '16px',
                  backgroundColor: '#e0e7ff',
                  color: '#4f46e5'
                }}>
                  <FaHospital size={24} />
                </div>
                <div>
                  <h3 style={{ 
                    fontSize: '24px',
                    fontWeight: 600,
                    margin: '0 0 4px 0'
                  }}>
                    {loading ? <Spin size="small" /> : getFacilityCount()}
                  </h3>
                  <p style={{ 
                    fontSize: '14px',
                    color: '#6b7280',
                    margin: 0
                  }}>
                    Total Facilities
                  </p>
                </div>
              </div>
            </Card>
          </div>

          {/* Main Facilities Table */}
          <div className="parent-con" style={{ 
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            overflow: 'hidden'
          }}>
            <div className="data-table">
              <div className="top" style={{ 
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '20px',
                borderBottom: '1px solid #f0f0f0'
              }}>
                <div className="span">
                  <h3 style={{ 
                    fontSize: '18px',
                    fontWeight: 600,
                    margin: 0
                  }}>
                    Facilities Directory
                  </h3>
                </div>

                <Button
                  type="primary"
                  icon={<FaPlus />}
                  onClick={toggleAddModal}
                  style={{ 
                    backgroundColor: '#322659',
                    borderColor: '#322659'
                  }}
                >
                  Add Facility
                </Button>
              </div>

              <div className="filter-section" style={{ 
                display: 'flex',
                justifyContent: 'space-between',
                padding: '16px 20px',
                backgroundColor: '#f9fafb',
                borderBottom: '1px solid #f0f0f0',
                alignItems: 'center'
              }}>
                <Search
                  placeholder="Search facilities..."
                  allowClear
                  onSearch={handleSearch}
                  onChange={(e) => handleSearch(e.target.value)}
                  value={searchText}
                  style={{ width: 250 }}
                />
                
                <Space>
                  <Button onClick={fetchData}>
                    Refresh
                  </Button>
                </Space>
              </div>

              <div className="table-container" style={{ 
                padding: '0 20px 20px'
              }}>
                {loading ? (
                  <div style={{ 
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '300px',
                    flexDirection: 'column'
                  }}>
                    <BeatLoader color="#322659" />
                    <p style={{ marginTop: '16px', color: '#6b7280' }}>
                      Loading facilities data...
                    </p>
                  </div>
                ) : filteredData.length > 0 ? (
                  <DataTable 
                    key={allFacilities.length} 
                    onEdit={handleEdit} 
                    data={filteredData} 
                  />
                ) : (
                  <Empty 
                    description={
                      searchText ? "No facilities match your search" : "No facilities found"
                    } 
                    style={{ margin: '40px 0' }}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </Layout>

      {/* Modals */}
      {showModal && (
        <AddFacilityModal 
          editData={editData} 
          onClose={() => {
            setShowModal(false);
            setEditData(null);
          }}  
          onContentAdded={() => {
            setShowModal(false);
            fetchData();
          }} 
        />
      )}
      
      {showConfirmModal && (
        <ConfirmModal 
          title="Are you sure you want to delete this facility?"
          onAccept={deleteFacility}
          onCancel={() => setShowConfirmModal(false)}
        />
      )}
    </>
  );
}

export default Index;